<template>
    <Dialog :visible="visible" :header="currentEntry ? 'Edit Entry' : 'Add Entry'" :modal="true"
        class="p-fluid w-full max-w-md" @update:visible="emit('close')">
        <div v-if="slot" class="mb-2 bg-gray-50 p-2 rounded text-sm border border-gray-100">
            <div class="flex justify-between">
                <span class="font-bold text-gray-700">{{ day }}</span>
                <span class="font-mono text-gray-600 text-xs">{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time)
                }}</span>
            </div>
            <div class="text-xs text-gray-500 mt-0.5">{{ slot.name }} ({{ slot.type }})</div>
        </div>

        <div class="flex flex-col gap-2">
            <div class="field">
                <label class="font-semibold block mb-1 text-sm">{{ labels.SUBJECT }}</label>
                <Select v-model="form.teachable_id" :options="teachableOptions" optionLabel="label" optionValue="value"
                    :placeholder="`Select ${labels.SUBJECT}`" class="w-full" filter
                    :disabled="!canEdit" />
            </div>

            <div class="field">
                <label class="font-semibold block mb-1 text-sm">{{ labels.TEACHER }}</label>
                <Select v-model="form.staff_id" :options="staffOptions" optionLabel="label" optionValue="value"
                    :placeholder="`Select ${labels.TEACHER}`" class="w-full" filter
                    :disabled="!canEdit" />
            </div>

            <div class="field">
                <label class="font-semibold block mb-1 text-sm">Room (Optional)</label>
                <InputText v-model="form.room_name" placeholder="e.g. Lab 1" class="w-full" :disabled="!canEdit" />
            </div>

            <div class="field">
                <label class="font-semibold block mb-1 text-sm">Custom Title (Optional)</label>
                <InputText v-model="form.custom_title" :placeholder="`Override ${labels.SUBJECT.toLowerCase()} name`" class="w-full" :disabled="!canEdit" />
                <small class="text-gray-400 text-xs">Leave blank to use {{ labels.SUBJECT.toLowerCase() }} name.</small>
            </div>

            <div v-if="!currentEntry && canEdit" class="field">
                <div class="flex items-center gap-2 mb-1.5">
                    <Checkbox v-model="batchMode" :binary="true" inputId="batchMode" />
                    <label for="batchMode" class="font-semibold text-sm cursor-pointer">Also apply to other days</label>
                </div>
                <div v-if="batchMode" class="flex flex-wrap gap-1.5">
                    <div v-for="d in availableDays" :key="d"
                        class="px-2 py-0.5 text-xs rounded-full cursor-pointer transition-all border"
                        :class="batchDays.includes(d)
                            ? 'bg-primary/10 border-primary text-primary font-medium'
                            : 'bg-gray-100 border-gray-200 text-gray-500'"
                        @click="toggleBatchDay(d)">
                        {{ d.slice(0, 3) }}
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-between w-full">
                <Button v-if="currentEntry && can(currentUser, AppPermissions.TimeTable.Actions.Delete, AppPermissions.TimeTable.Resource)"
                    label="Delete" icon="pi pi-trash" class="p-button-danger p-button-text p-button-sm"
                    @click="handleDelete" :loading="deleting" />
                <div v-else></div>

                <div class="flex gap-1.5">
                    <Button label="Cancel" class="p-button-text p-button-sm" @click="emit('close')" />
                    <Button v-if="canEdit" label="Save" size="small" class="p-button-primary !bg-primary !border-primary" @click="handleSave"
                        :loading="saving" :disabled="!isValid" />
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import {
  useGetAllSubjectsQuery,
  useGetAllCoursesQuery,
  useGetAllStaffQuery,
  useUpsertTimetableEntryMutation,
  useDeleteTimetableEntryMutation,
} from '@/types/vue-apollo-helpers';
import type { TimetableTimeSlot, TimetableEntry } from '@/types/composition-functions';
import { TeachableType } from '@/types/composition-functions';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { teachableName } from '@/utils/teachable';
import { ALL_DAYS } from '@/constants/timetable-presets';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';

import { extractGraphQLError } from '@/utils/graphqlError';
const confirm = useConfirm();
const toast = useToast();

const userStore = useUserStore();
const currentUser = computed(() => userStore.currentUser);
const { labels: labelsRef } = useSchoolLabels();
const labels = computed(() => labelsRef.value);
const { isTertiary } = useSchoolFeatures();

const props = defineProps<{
    visible: boolean;
    slot: TimetableTimeSlot | null;
    day: string;
    timetableId: string;
    currentEntry?: TimetableEntry | null;
    classroomId?: string;
}>();

const emit = defineEmits(['close', 'saved', 'deleted']);

const form = ref({
  teachable_id: '' as string | '',
  teachable_type: TeachableType.Subject as TeachableType,
  staff_id: '',
  room_name: '',
  custom_title: '',
});

const batchMode = ref(false);
const batchDays = ref<string[]>([]);

const availableDays = computed(() =>
  [...ALL_DAYS].filter(d => d !== props.day?.toUpperCase()),
);

function toggleBatchDay(day: string) {
  const idx = batchDays.value.indexOf(day);
  if (idx >= 0) batchDays.value.splice(idx, 1);
  else batchDays.value.push(day);
}

const { result: subjectsResult } = useGetAllSubjectsQuery(
  {},
  { enabled: computed(() => !isTertiary.value) },
);
const { result: coursesResult } = useGetAllCoursesQuery(
  { first: 200, page: 1 },
  { enabled: isTertiary },
);
const { result: staffResult } = useGetAllStaffQuery();

const { mutate: upsertEntry, loading: saving } = useUpsertTimetableEntryMutation();
const { mutate: deleteEntry, loading: deleting } = useDeleteTimetableEntryMutation();

const teachableOptions = computed(() => {
  if (isTertiary.value) {
    return coursesResult.value?.listSchoolCourses?.data?.map((c) => ({
      label: `${c.course_code} — ${c.course_title}`,
      value: c.id,
    })) || [];
  }
  return subjectsResult.value?.listSchoolSubjects?.map((s) => ({
    label: s!.name,
    value: s!.id,
  })) || [];
});

const staffOptions = computed(() => staffResult.value?.getStaff?.data.map((s) => ({
  label: s.user?.name || 'Unknown',
  value: s.id,
})) || []);

const isValid = computed(() => form.value.teachable_id || form.value.custom_title);

const canEdit = computed(() => {
  if (props.currentEntry) {
    return can(currentUser.value, AppPermissions.TimeTable.Actions.Update, AppPermissions.TimeTable.Resource);
  } else {
    return can(currentUser.value, AppPermissions.TimeTable.Actions.Create, AppPermissions.TimeTable.Resource);
  }
});

const defaultTeachableType = computed(() => isTertiary.value ? TeachableType.Course : TeachableType.Subject);

watch(() => props.visible, (newVal) => {
  if (newVal) {
    batchMode.value = false;
    batchDays.value = [];
    if (props.currentEntry) {
      const t = props.currentEntry.teachable;
      const inferredType = t && 'course_title' in t
        ? TeachableType.Course
        : t && 'name' in t
          ? TeachableType.Subject
          : defaultTeachableType.value;
      form.value = {
        teachable_id: t?.id || '',
        teachable_type: inferredType,
        staff_id: props.currentEntry.staff?.id || '',
        room_name: props.currentEntry.room_name || '',
        custom_title: props.currentEntry.custom_title || '',
      };
    } else {
      form.value = {
        teachable_id: '',
        teachable_type: defaultTeachableType.value,
        staff_id: '',
        room_name: '',
        custom_title: '',
      };
    }
  }
});

watch(defaultTeachableType, (next) => {
  if (!props.currentEntry) {
    form.value.teachable_type = next;
    form.value.teachable_id = '';
  }
});

function formatTime(time: string) {
  if (!time) return '';
  const [h, m] = time.split(':');
  return `${h}:${m}`;
}

async function handleSave() {
  try {
    const teachableId = form.value.teachable_id || null;
    const baseInput = {
      timetable_id: props.timetableId,
      timetable_time_slot_id: props.slot!.id,
      teachable_id: teachableId,
      teachable_type: teachableId ? form.value.teachable_type : null,
      staff_id: form.value.staff_id || null,
      room_name: form.value.room_name,
      custom_title: form.value.custom_title,
    };

    await upsertEntry({
      input: { ...baseInput, id: props.currentEntry?.id, day_of_week: props.day },
    });

    if (batchMode.value && !props.currentEntry && batchDays.value.length > 0) {
      for (const day of batchDays.value) {
        await upsertEntry({
          input: { ...baseInput, day_of_week: day },
        });
      }
    }

    emit('saved');
    emit('close');
  } catch {
    // Error not surfaced here; the save simply aborts.
  }
}

function handleDelete() {
  if (!props.currentEntry?.id) return;
  const entryId = props.currentEntry.id;
  const label = teachableName(props.currentEntry) || props.currentEntry.custom_title || 'this entry';
  confirm.require({
    message: `Delete ${label} from the timetable?`,
    header: 'Delete Entry',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try {
        await deleteEntry({ id: entryId });
        toast.add({ severity: 'success', summary: 'Entry deleted', life: 2500 });
        emit('deleted');
        emit('close');
      } catch (e) {
        toast.add({ severity: 'error', summary: 'Delete failed', detail: extractGraphQLError(e, 'Could not delete the entry.'), life: 4000 });
      }
    },
  });
}

defineExpose({ teachableName });
</script>
